// Levels of Hiccup robot. The id is fixed (progress is saved on it) – never change it once the level is released.
import { defineLevels } from '../../core/registry';
import { on } from '../../core/level-helpers';
import type { LevelData, ObjSpec, SkinType, StaticData } from '../../core/types';
import { terms, type Pt } from './shared';

const L: LevelData[] = [];
const P = 15;
// Cables have no x/y, only pts (registered in ObjDataTypes in world.ts).
const cab = (...pts: Pt[]): ObjSpec => ({ t: 'robot_cable', pts });
/** Shelf/wall from [x, y, w, h = 22, a = 0] */
const hy = (s: number[], w?: SkinType): StaticData => ({ x: s[0], y: s[1], w: s[2], h: s[3] || 22, a: s[4] || 0, skin: w || 'robot_shelf' });
// From level 9 on: the sockets no longer blink, and loose cable pieces lie around (false gaps).
// The player has to follow the cables round and find the gap that closes the circuit.
const loose = (...pts: Pt[]) => cab(...pts);
function add(level: LevelData) {
  if (L.length >= 8) for (const o of level.objs!) if (o.t === 'robot_cable') o.quiet = true;
  L.push(level);
}

// 1 – The gate – lay a wire across the gap so the gate gets power and opens (new: wire)
{
  const s1 = hy([380, 400, 640, 22, 4]);
  const door = { t: 'robot_gate', x: 640, y: 350, h: 110 } satisfies ObjSpec, [dl, dr] = terms(door);
  const bat = { t: 'robot_batt', x: 380, y: 150 } satisfies ObjSpec, [bl, br] = terms(bat);
  add({
    id: 'gate', par: 1, tools: { robot_wire: 1 }, learn: ['electricity', 'circuit'], intro: 'robot_wire',
    statics: [s1, hy([860, 624, 300, 26], 'robot_table')],
    balls: [on(s1, -270, P)],
    goal: { type: 'bowl', x: 850, y: 611, w: 170 },
    objs: [bat, door, cab(br, [540, 150], [540, dl[1]], dl), cab(dr, [780, dr[1]], [780, 90], [625, 90]), cab([495, 90], [250, 90], [250, bl[1]], bl), { t: 'duck', x: 770, y: 470 }],
    solution: [{ t: 'robot_wire', x: 560, y: 95, a: 0 }],
  });
}

// 2 – The switch – the ball presses the switch down, the lamp lights and the gate opens (new: switch, lamp)
{
  const sA = hy([200, 200, 300, 22, 8]), sB = hy([690, 262, 500, 22, 4]);
  const sw = { t: 'robot_switch', x: 560, y: 242 } satisfies ObjSpec, [kl, kr] = terms(sw);
  const door = { t: 'robot_gate', x: 840, y: 215, h: 90 } satisfies ObjSpec, [dl, dr] = terms(door);
  const bat = { t: 'robot_batt', x: 980, y: 102 } satisfies ObjSpec, [bl, br] = terms(bat);
  const lam = { t: 'robot_lamp', x: 690, y: 80 } satisfies ObjSpec, [ll, lr] = terms(lam);
  add({
    id: 'switch', par: 1, tools: { plank: 1 }, learn: ['switch', 'circuit'], intro: 'robot_switch',
    statics: [sA, sB, hy([1040, 572, 220, 26], 'robot_table'), hy([1135, 420, 22, 280], 'robot_wall')],
    balls: [on(sA, -110, P)],
    goal: { type: 'bowl', x: 1040, y: 559, w: 160 },
    objs: [bat, lam, sw, door, cab(bl, lr), cab(ll, [kl[0], ll[1]], kl), cab(kr, [kr[0], 180], [740, 180], [740, dl[1]], dl), cab(dr, [1090, dr[1]], [1090, br[1]], br), { t: 'duck', x: 980, y: 380 }],
    solution: [{ t: 'plank', x: 400, y: 230, a: 15 }],
  });
}

// 3 – The belt – power makes the motor run the belt uphill (new: belt)
{
  const s1 = hy([250, 230, 270, 22, 10]);
  const belt = { t: 'robot_belt', x: 540, y: 520, w: 400, a: -14, dir: 1 } satisfies ObjSpec, [cl, cr] = terms(belt);
  const bat = { t: 'robot_batt', x: 250, y: 650 } satisfies ObjSpec, [bl, br] = terms(bat);
  add({
    id: 'belt', par: 1, tools: { robot_wire: 1 }, learn: ['electricity', 'motor'],
    statics: [s1, hy([330, 535, 20, 90], 'robot_wall'), hy([850, 580, 220, 26], 'robot_table')],
    balls: [on(s1, -100, P)],
    goal: { type: 'bowl', x: 850, y: 567, w: 170 },
    objs: [bat, belt, cab(cr, [620, cr[1]], [620, 750], [140, 750], [140, bl[1]], bl), cab(br, [345, br[1]]), cab(cl, [480, cl[1]], [480, 650]), { t: 'duck', x: 700, y: 380 }],
    solution: [{ t: 'robot_wire', x: 410, y: 650, a: 0 }],
  });
}

// 4 – String or wire – only the metal wire carries power to the lift; the wool string is a decoy (new: string, lift)
{
  const s1 = hy([250, 480, 380, 22, 12]), s2 = hy([800, 330, 380, 22, 3]);
  const lift = { t: 'robot_lift', x: 530, y: 600, top: 278, w: 120, a: 6 } satisfies ObjSpec, [fl, fr] = terms(lift);
  const bat = { t: 'robot_batt', x: 260, y: 700 } satisfies ObjSpec, [bl, br] = terms(bat);
  add({
    id: 'string-or-wire', par: 1, tools: { robot_string: 1, robot_wire: 1 }, learn: ['materials', 'electricity'], intro: 'robot_string',
    statics: [s1, s2, hy([606, 480, 20, 320], 'robot_wall'), hy([1050, 572, 200, 26], 'robot_table'), hy([1138, 430, 22, 260], 'robot_wall')],
    balls: [on(s1, -150, P)],
    goal: { type: 'bowl', x: 1045, y: 559, w: 160 },
    objs: [bat, lift, cab(br, [360, br[1]]), cab(fl, [fl[0], 700]), cab(fr, [fr[0], 760], [150, 760], [150, bl[1]], bl), { t: 'duck', x: 800, y: 230 }],
    solution: [{ t: 'robot_wire', x: 425, y: 700, a: 0 }],
    fails: [[{ t: 'robot_string', x: 425, y: 700, a: 0 }]],
  });
}

// 5 – The battery – no battery, no power; a wire is not a battery (new: battery, holder, fan)
{
  const s1 = hy([470, 400, 460, 22, 0]);
  const fan = { t: 'robot_fan', x: 300, y: 322, a: 0, len: 380, w: 140, power: 0.5 } satisfies ObjSpec, [fl, fr] = terms(fan);
  const hol = { t: 'robot_holder', x: 300, y: 150 } satisfies ObjSpec;
  add({
    id: 'battery', par: 1, tools: { robot_wire: 1, robot_battery: 1 }, learn: ['electricity', 'battery'], intro: 'robot_battery',
    statics: [s1, hy([830, 612, 220, 26], 'robot_table'), hy([935, 480, 22, 240], 'robot_wall')],
    balls: [{ x: 420, y: 373, r: P }],
    goal: { type: 'bowl', x: 830, y: 599, w: 170 },
    objs: [hol, fan, cab(fl, [fl[0], 150]), cab(fr, [fr[0], 150]), { t: 'duck', x: 560, y: 250 }],
    solution: [{ t: 'robot_battery', x: 300, y: 150, a: 0 }],
    fails: [[{ t: 'robot_wire', x: 300, y: 150, a: 0 }]],
  });
}

// 6 – All the way round – two gaps, both must be closed
{
  const s1 = hy([360, 300, 660, 22, 5]);
  const door = { t: 'robot_gate', x: 640, y: 264, h: 100 } satisfies ObjSpec, [dl, dr] = terms(door);
  const bat = { t: 'robot_batt', x: 300, y: 120 } satisfies ObjSpec, [bl, br] = terms(bat);
  add({
    id: 'all-the-way-round', par: 2, tools: { robot_wire: 2, robot_string: 1 }, learn: ['circuit'],
    statics: [s1, hy([740, 612, 260, 26], 'robot_table'), hy([838, 470, 22, 260], 'robot_wall')],
    balls: [on(s1, -290, P)],
    goal: { type: 'bowl', x: 740, y: 599, w: 170 },
    objs: [bat, door, cab(br, [400, 120]), cab([530, 120], [560, 120], [560, dl[1]], dl), cab(dr, [760, dr[1]], [760, 60], [700, 60]), cab([570, 60], [170, 60], [170, bl[1]], bl), { t: 'duck', x: 850, y: 470 }],
    solution: [{ t: 'robot_wire', x: 465, y: 122, a: 0 }, { t: 'robot_wire', x: 635, y: 62, a: 0 }],
    fails: [[{ t: 'robot_wire', x: 465, y: 122, a: 0 }], [{ t: 'robot_wire', x: 635, y: 62, a: 0 }], [{ t: 'robot_wire', x: 465, y: 122, a: 0 }, { t: 'robot_string', x: 635, y: 62, a: 0 }]],
  });
}

// 7 – The hiccup robot – with power the robot hiccups and its head throws the ball up (new: hiccup robot)
{
  const s1 = hy([300, 380, 320, 22, 3]), s2 = hy([880, 330, 340, 22, 6]);
  const rob = { t: 'robot_hiccup', x: 545, y: 640, pop: [3, -12] } satisfies ObjSpec, [el, er] = terms(rob);
  const bat = { t: 'robot_batt', x: 480, y: 740 } satisfies ObjSpec, [bl, br] = terms(bat);
  add({
    id: 'hiccup-robot', par: 1, tools: { robot_string: 1, robot_wire: 1, plank: 1 }, learn: ['electricity', 'motor'], intro: 'robot_hiccup',
    statics: [s1, s2, hy([545, 652, 200, 24], 'robot_table'), hy([1050, 572, 200, 26], 'robot_table'), hy([1138, 430, 22, 260], 'robot_wall')],
    balls: [on(s1, -130, P)],
    goal: { type: 'bowl', x: 1045, y: 559, w: 160 },
    objs: [bat, rob, cab(el, [360, el[1]], [360, bl[1]], bl), cab(er, [680, er[1]], [680, 690]), cab(br, [810, br[1]], [810, 690]), { t: 'duck', x: 620, y: 180 }],
    solution: [{ t: 'robot_wire', x: 745, y: 690, a: 0 }],
    fails: [[{ t: 'robot_string', x: 745, y: 690, a: 0 }]],
  });
}

// 8 – Which wire? – two circuits, only the one with the gate helps the ball
{
  const s1 = hy([380, 400, 640, 22, 4]);
  const door = { t: 'robot_gate', x: 640, y: 350, h: 110 } satisfies ObjSpec, [dl, dr] = terms(door);
  const bat = { t: 'robot_batt', x: 380, y: 150 } satisfies ObjSpec, [bl, br] = terms(bat);
  const bat2 = { t: 'robot_batt', x: 300, y: 660 } satisfies ObjSpec, [b2l, b2r] = terms(bat2);
  const lam = { t: 'robot_lamp', x: 300, y: 530 } satisfies ObjSpec, [ll, lr] = terms(lam);
  add({
    id: 'which-wire', par: 1, tools: { robot_wire: 1 }, learn: ['circuit'],
    statics: [s1, hy([860, 624, 300, 26], 'robot_table')],
    balls: [on(s1, -270, P)],
    goal: { type: 'bowl', x: 850, y: 611, w: 170 },
    objs: [bat, door, bat2, lam, cab(br, [540, 150], [540, dl[1]], dl), cab(dr, [780, dr[1]], [780, 90], [625, 90]), cab([495, 90], [250, 90], [250, bl[1]], bl),
      cab(ll, [170, ll[1]], [170, b2l[1]], b2l), cab(lr, [420, lr[1]]), cab(b2r, [560, b2r[1]], [560, lr[1]]), { t: 'duck', x: 430, y: 470 }],
    solution: [{ t: 'robot_wire', x: 560, y: 95, a: 0 }],
    fails: [[{ t: 'robot_wire', x: 490, y: 552, a: 0 }]],
  });
}

// 9 – Battery and wire – the circuit lacks both power and a piece of wire; sockets stop blinking, loose cables as false gaps
{
  const s1 = hy([950, 230, 270, 22, -10]);
  const belt = { t: 'robot_belt', x: 660, y: 520, w: 400, a: 14, dir: -1 } satisfies ObjSpec, [cl, cr] = terms(belt);
  const hol = { t: 'robot_holder', x: 950, y: 650 } satisfies ObjSpec;
  add({
    id: 'battery-and-wire', par: 2, tools: { robot_battery: 1, robot_wire: 2, robot_string: 1 }, learn: ['battery', 'circuit'],
    statics: [s1, hy([870, 535, 20, 90], 'robot_wall'), hy([350, 580, 220, 26], 'robot_table')],
    balls: [on(s1, 100, P)],
    goal: { type: 'bowl', x: 350, y: 567, w: 170 },
    objs: [hol, belt, cab(cl, [580, cl[1]], [580, 750], [1060, 750], [1060, 650], [1010, 650]), cab([890, 650], [855, 650]), cab(cr, [720, cr[1]], [720, 650]),
      loose([110, 680], [110, 760], [220, 760]), loose([375, 760], [480, 760], [480, 680]), { t: 'duck', x: 500, y: 380 }],
    solution: [{ t: 'robot_battery', x: 950, y: 650, a: 0 }, { t: 'robot_wire', x: 788, y: 650, a: 0 }],
    fails: [[{ t: 'robot_battery', x: 950, y: 650, a: 0 }, { t: 'robot_wire', x: 297, y: 760, a: 0 }], [{ t: 'robot_battery', x: 950, y: 650, a: 0 }], [{ t: 'robot_wire', x: 788, y: 650, a: 0 }], [{ t: 'robot_battery', x: 950, y: 650, a: 0 }, { t: 'robot_string', x: 788, y: 650, a: 0 }]],
  });
}

// 10 – The copper ball – metal conducts, the copper ball holds the switch closed so the lift runs (switch with hold)
{
  const sA = hy([180, 180, 240, 22, 8]), sB = hy([520, 260, 260, 22, 4]);
  const sw = { t: 'robot_switch', x: 595, y: 254, hold: true } satisfies ObjSpec, [kl, kr] = terms(sw);
  const lift = { t: 'robot_lift', x: 840, y: 640, top: 306, w: 120, a: 6 } satisfies ObjSpec, [fl, fr] = terms(lift);
  const bat = { t: 'robot_batt', x: 560, y: 720 } satisfies ObjSpec, [bl, br] = terms(bat);
  add({
    id: 'copper-ball', par: 1, tools: { plank: 1, robot_string: 1 }, learn: ['materials', 'switch'],
    statics: [sA, sB, hy([638, 240, 16, 44], 'robot_wall'), hy([916, 500, 20, 300], 'robot_wall'), hy([1000, 360, 150, 22, 10]), hy([1060, 622, 170, 26], 'robot_table'), hy([1140, 480, 22, 240], 'robot_wall')],
    balls: [{ x: 850, y: 600, r: P }, on(sA, -80, 13, { helper: true })],
    goal: { type: 'bowl', x: 1058, y: 609, w: 140 },
    objs: [bat, sw, lift, cab(kl, [kl[0], 330], [440, 330], [440, bl[1]], bl), cab(kr, [kr[0], 330], [700, 330], [700, fl[1]], fl), cab(fr, [fr[0], 760], [br[0], 760], br), { t: 'duck', x: 840, y: 240 }],
    solution: [{ t: 'plank', x: 345, y: 225, a: 20 }],
    fails: [[{ t: 'robot_string', x: 595, y: 250, a: 0 }], [{ t: 'plank', x: 345, y: 420, a: 0 }]],
  });
}

// 11 – Robot and fan – two machines, two circuits, both need to go all the way round
{
  const s1 = hy([200, 440, 260, 22, 8]), s2 = hy([860, 330, 340, 22, 6]);
  const rob = { t: 'robot_hiccup', x: 400, y: 700, pop: [0, -15] } satisfies ObjSpec, [rl, rr] = terms(rob);
  const bat1 = { t: 'robot_batt', x: 400, y: 768 } satisfies ObjSpec, [b1l, b1r] = terms(bat1);
  const fan = { t: 'robot_fan', x: 200, y: 200, a: 0, len: 520, w: 150, power: 1.2 } satisfies ObjSpec, [fl, fr] = terms(fan);
  const bat2 = { t: 'robot_batt', x: 200, y: 80 } satisfies ObjSpec, [b2l, b2r] = terms(bat2);
  add({
    id: 'robot-and-fan', par: 2, tools: { robot_wire: 3, robot_string: 1 }, learn: ['circuit', 'motor'],
    statics: [s1, s2, hy([400, 712, 200, 24], 'robot_table'), hy([1060, 572, 170, 26], 'robot_table'), hy([1140, 430, 22, 260], 'robot_wall')],
    balls: [on(s1, -90, P)],
    goal: { type: 'bowl', x: 1058, y: 559, w: 140 },
    objs: [bat1, rob, bat2, fan, cab(rl, [290, rl[1]], [290, b1l[1]], b1l), cab(rr, [540, rr[1]], [540, 700]), cab(b1r, [680, b1r[1]], [680, 700]),
      cab(fl, [90, fl[1]], [90, b2l[1]], b2l), cab(fr, [300, fr[1]], [300, 150]), cab(b2r, [440, b2r[1]], [440, 150]),
      loose([580, 150], [760, 150], [760, 230]), loose([820, 700], [820, 770], [980, 770]), { t: 'duck', x: 600, y: 100 }],
    solution: [{ t: 'robot_wire', x: 610, y: 700, a: 0 }, { t: 'robot_wire', x: 370, y: 150, a: 0 }],
    fails: [[{ t: 'robot_wire', x: 750, y: 700, a: 0 }, { t: 'robot_wire', x: 510, y: 150, a: 0 }], [{ t: 'robot_wire', x: 610, y: 700, a: 0 }, { t: 'robot_wire', x: 510, y: 150, a: 0 }], [{ t: 'robot_wire', x: 610, y: 700, a: 0 }], [{ t: 'robot_wire', x: 370, y: 150, a: 0 }], [{ t: 'robot_wire', x: 610, y: 700, a: 0 }, { t: 'robot_string', x: 370, y: 150, a: 0 }]],
  });
}

// 12 – Switch and wire – get the ball over the switch and close two gaps in the same circuit
{
  const sA = hy([1000, 200, 300, 22, -8]), sB = hy([510, 262, 500, 22, -4]);
  const sw = { t: 'robot_switch', x: 640, y: 242 } satisfies ObjSpec, [kl, kr] = terms(sw);
  const door = { t: 'robot_gate', x: 360, y: 215, h: 90 } satisfies ObjSpec, [dl, dr] = terms(door);
  const bat = { t: 'robot_batt', x: 220, y: 102 } satisfies ObjSpec, [bl, br] = terms(bat);
  const lam = { t: 'robot_lamp', x: 560, y: 80 } satisfies ObjSpec, [ll, lr] = terms(lam);
  add({
    id: 'switch-and-wire', par: 3, tools: { plank: 1, robot_wire: 2, robot_string: 1 }, learn: ['switch', 'circuit'],
    statics: [sA, sB, hy([160, 572, 220, 26], 'robot_table'), hy([65, 420, 22, 280], 'robot_wall')],
    balls: [on(sA, 110, P)],
    goal: { type: 'bowl', x: 160, y: 559, w: 160 },
    objs: [bat, lam, sw, door, cab(br, [340, 102]), cab(ll, [470, 102]), cab(lr, [kr[0], lr[1]], kr), cab(kl, [kl[0], 180]), cab([460, 180], [460, dr[1]], dr), cab(dl, [110, dl[1]], [110, bl[1]], bl), { t: 'duck', x: 560, y: 420 }],
    solution: [{ t: 'plank', x: 800, y: 230, a: -15 }, { t: 'robot_wire', x: 405, y: 102, a: 0 }, { t: 'robot_wire', x: 531, y: 180, a: 0 }],
    fails: [[{ t: 'plank', x: 800, y: 230, a: -15 }, { t: 'robot_wire', x: 405, y: 102, a: 0 }], [{ t: 'plank', x: 800, y: 230, a: -15 }, { t: 'robot_wire', x: 531, y: 180, a: 0 }], [{ t: 'plank', x: 800, y: 230, a: -15 }, { t: 'robot_string', x: 405, y: 102, a: 0 }, { t: 'robot_wire', x: 531, y: 180, a: 0 }], [{ t: 'robot_wire', x: 405, y: 102, a: 0 }, { t: 'robot_wire', x: 531, y: 180, a: 0 }]],
  });
}

// 13 – The lift – build the way down to the lift, put the battery in and close the gap
{
  const s1 = hy([200, 420, 260, 22, 10]), s2 = hy([800, 330, 380, 22, 3]);
  const lift = { t: 'robot_lift', x: 530, y: 600, top: 278, w: 120, a: 6 } satisfies ObjSpec, [fl, fr] = terms(lift);
  const hol = { t: 'robot_holder', x: 260, y: 700 } satisfies ObjSpec;
  add({
    id: 'lift', par: 3, tools: { plank: 1, robot_battery: 1, robot_wire: 2, robot_string: 1 }, learn: ['battery', 'circuit'],
    statics: [s1, s2, hy([606, 480, 20, 320], 'robot_wall'), hy([1050, 572, 200, 26], 'robot_table'), hy([1138, 430, 22, 260], 'robot_wall')],
    balls: [on(s1, -100, P)],
    goal: { type: 'bowl', x: 1045, y: 559, w: 160 },
    objs: [hol, lift, cab([320, 700], [360, 700]), cab(fl, [fl[0], 700]), cab(fr, [fr[0], 760], [150, 760], [150, 700], [200, 700]),
      loose([660, 690], [660, 750], [760, 750]), loose([905, 750], [1010, 750], [1010, 690]), { t: 'duck', x: 800, y: 230 }],
    solution: [{ t: 'plank', x: 400, y: 480, a: 30 }, { t: 'robot_battery', x: 260, y: 700, a: 0 }, { t: 'robot_wire', x: 425, y: 700, a: 0 }],
    fails: [[{ t: 'plank', x: 400, y: 480, a: 30 }, { t: 'robot_battery', x: 260, y: 700, a: 0 }, { t: 'robot_wire', x: 832, y: 750, a: 0 }], [{ t: 'plank', x: 400, y: 480, a: 30 }, { t: 'robot_battery', x: 260, y: 700, a: 0 }], [{ t: 'plank', x: 400, y: 480, a: 30 }, { t: 'robot_wire', x: 425, y: 700, a: 0 }], [{ t: 'robot_battery', x: 260, y: 700, a: 0 }, { t: 'robot_wire', x: 425, y: 700, a: 0 }]],
  });
}

// 14 – Nut and spring – the heavy nut presses the switch, the spring throws the ball up
{
  const s1 = hy([200, 250, 300, 22, 8]), s2 = hy([760, 420, 360, 22, 4]), sS = hy([150, 560, 180, 22, 0]);
  const sw = { t: 'robot_switch', x: 150, y: 549 } satisfies ObjSpec, [kl, kr] = terms(sw);
  const door = { t: 'robot_gate', x: 800, y: 365, h: 90 } satisfies ObjSpec, [dl, dr] = terms(door);
  const bat = { t: 'robot_batt', x: 1000, y: 150 } satisfies ObjSpec, [bl, br] = terms(bat);
  add({
    id: 'nut-and-spring', par: 3, tools: { stone: 1, pad: 1, robot_wire: 2, plank: 1 }, learn: ['switch', 'spring', 'gravity'],
    statics: [s1, s2, sS, hy([928, 560, 22, 260], 'robot_wall'), hy([1040, 572, 200, 26], 'robot_table'), hy([1138, 430, 22, 260], 'robot_wall')],
    balls: [on(s1, -110, P)],
    goal: { type: 'bowl', x: 1040, y: 559, w: 160 },
    objs: [bat, sw, door, cab(dr, [900, dr[1]], [900, bl[1]], bl), cab(br, [1100, br[1]], [1100, 80], [620, 80]), cab([490, 80], [40, 80], [40, kl[1]], kl), cab(kr, [250, kr[1]], [250, 330], [700, 330], [700, dl[1]], dl),
      loose([330, 150], [330, 195], [430, 195]), loose([575, 195], [680, 195], [680, 150]), { t: 'duck', x: 560, y: 300 }],
    solution: [{ t: 'stone', x: 150, y: 420, a: 0 }, { t: 'pad', x: 560, y: 540, a: 15 }, { t: 'robot_wire', x: 555, y: 80, a: 0 }],
    fails: [[{ t: 'stone', x: 150, y: 420, a: 0 }, { t: 'pad', x: 560, y: 540, a: 15 }, { t: 'robot_wire', x: 502, y: 195, a: 0 }], [{ t: 'pad', x: 560, y: 540, a: 15 }, { t: 'robot_wire', x: 555, y: 80, a: 0 }], [{ t: 'stone', x: 150, y: 420, a: 0 }, { t: 'pad', x: 560, y: 540, a: 15 }]],
  });
}

// 15 – The master workshop – gate and belt on one battery; a long gap needs two wires end to end
{
  const s1 = hy([230, 220, 360, 22, 5]);
  const door = { t: 'robot_gate', x: 360, y: 169, h: 90 } satisfies ObjSpec, [dl, dr] = terms(door);
  const hol = { t: 'robot_holder', x: 740, y: 102 } satisfies ObjSpec;
  const belt = { t: 'robot_belt', x: 822, y: 560, w: 320, a: -12, dir: 1 } satisfies ObjSpec, [cl, cr] = terms(belt);
  add({
    id: 'master-workshop', par: 4, tools: { robot_wire: 3, robot_battery: 1, plank: 2, pad: 1, robot_string: 1 }, learn: ['circuit', 'battery', 'motor'],
    statics: [s1, hy([652, 560, 20, 90], 'robot_wall'), hy([1068, 612, 160, 26], 'robot_table'), hy([1140, 470, 22, 240], 'robot_wall')],
    balls: [on(s1, -150, P)],
    goal: { type: 'bowl', x: 1068, y: 599, w: 130 },
    objs: [hol, door, belt, cab(dr, [420, 102]), cab([680, 102], [670, 102]), cab([800, 102], [1168, 102], [1168, 720], [cr[0], 720], cr), cab(cl, [cl[0], 760], [34, 760], [34, 60], [260, 60], [260, dl[1]], dl), { t: 'duck', x: 960, y: 380 }],
    solution: [{ t: 'robot_battery', x: 740, y: 102, a: 0 }, { t: 'robot_wire', x: 500, y: 102, a: 0 }, { t: 'robot_wire', x: 590, y: 102, a: 0 }, { t: 'plank', x: 540, y: 480, a: 30 }],
    fails: [[{ t: 'robot_battery', x: 740, y: 102, a: 0 }, { t: 'robot_wire', x: 500, y: 102, a: 0 }, { t: 'plank', x: 540, y: 480, a: 30 }], [{ t: 'robot_battery', x: 740, y: 102, a: 0 }, { t: 'robot_wire', x: 590, y: 102, a: 0 }, { t: 'plank', x: 540, y: 480, a: 30 }], [{ t: 'robot_battery', x: 740, y: 102, a: 0 }, { t: 'robot_wire', x: 545, y: 102, a: 0 }, { t: 'plank', x: 540, y: 480, a: 30 }], [{ t: 'robot_wire', x: 500, y: 102, a: 0 }, { t: 'robot_wire', x: 590, y: 102, a: 0 }, { t: 'plank', x: 540, y: 480, a: 30 }], [{ t: 'robot_battery', x: 740, y: 102, a: 0 }, { t: 'robot_wire', x: 500, y: 102, a: 0 }, { t: 'robot_wire', x: 590, y: 102, a: 0 }]],
  });
}

export const levels = defineLevels(L);
